import React, { useEffect, useRef, useCallback } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { useHistory } from 'react-router-dom';

import * as StampCardService from '../../api/stampCardService';
import * as CouponService from '../../api/couponService';
import * as StoreService from '../../api/storeService';

import parseDeepLink, { ScanType } from '../../lib/deeplink';

interface QRScannerProps {
  onError?: (errorMessage: string) => void;
}

const QRScanner: React.FC<QRScannerProps> = ({ onError }) => {
  const scannerRef = useRef<HTMLDivElement>(null);
  const scannerInstance = useRef<Html5Qrcode | null>(null);
  const history = useHistory();
  const hasScanned = useRef(false);

  const handleSingleUseCode = useCallback(
    async (
      singleUseCode: string,
      merchantId: string,
      stampNum: string,
      storeId: string,
    ) => {
      console.log('SingleUseCode: ', singleUseCode);
      console.log('storeId: ', storeId);
      console.log('stampNum: ', stampNum);
      console.log('merchantId: ', merchantId);
      if (singleUseCode && storeId && stampNum && merchantId) {
        console.log('Redeeming single use code...');
        try {
          const resRedeem = await StampCardService.creditStampCardSingleUse(
            singleUseCode,
            storeId,
            parseInt(stampNum),
            merchantId,
          );
          if (resRedeem && resRedeem.id) {
            history.replace(
              `/merchant/${merchantId}/stampNum/${stampNum}/stampCard/${resRedeem.id}/singleUseRedeem/status/success/msg/ok`,
            );
            history.go(0);
          }
        } catch (e: any) {
          console.log('ResRedeem Error: ', e);
          if (e && typeof e === 'object') {
            console.log(JSON.stringify(e));
            history.replace(
              `/merchant/${merchantId}/stampNum/${stampNum}/stampCard/0/singleUseRedeem/status/fail/msg/${e.data[0].detail}`,
            );
            history.go(0);
          }
        }
      }
    },
    [history],
  );

  const handleContactlessCode = useCallback(
    async (contactlessCode: string) => {
      console.log('ContactlessCode: ', contactlessCode);
      if (contactlessCode && contactlessCode.length > 0) {
        try {
          const resStore = await StoreService.getStoreByContactlessCode(
            contactlessCode.split('_')[0],
          );
          if (resStore && resStore.merchant.id) {
            try {
              const resRedeem =
                await StampCardService.creditStampCardContactless(
                  contactlessCode,
                );
              console.log('ResRedeem Contactless: ', resRedeem);
              if (resRedeem && resRedeem.id) {
                // successfully redeemed
                history.replace(
                  `/contactless/${contactlessCode}/merchant/${resStore.merchant.id}/stampCard/${resRedeem.id}/status/success/msg/ok`,
                );
                history.go(0);
              }
            } catch (e: any) {
              // failed to redeem
              if (e && e.data && e.data[0].detail) {
                if (e.data[0].detail === 'Stamp count exceeds store limit') {
                  history.replace(
                    `/contactless/${contactlessCode}/merchant/${resStore.merchant.id}/stampCard/0/status/fail/msg/exceeds_store_limit`,
                  );
                } else {
                  history.replace(
                    `/contactless/${contactlessCode}/merchant/${resStore.merchant.id}/stampCard/0/status/fail/msg/${e.data[0].detail}`,
                  );
                }
                history.go(0);
              }
            }
          }
        } catch (e) {
          console.log('Redeem Contactless Error: ', e);
          history.replace(
            `/contactless/${contactlessCode}/merchant/0/stampCard/0/status/fail/msg/invalid_contactless_code`,
          );
          history.go(0);
        }
      }
    },
    [history],
  );

  const handleCouponCode = useCallback(
    async (couponCode: string) => {
      console.log('CouponCode: ', couponCode);
      if (couponCode && couponCode.length > 0) {
        // check if coupon code has been redeemed
        try {
          const res = await CouponService.get(couponCode);
          console.log('Fetch Coupon: ', res);
          if (res.redeemed) {
            history.replace(
              `/coupon/${couponCode}/merchant/${res.company.id}/status/fail/msg/redeemed_before`,
            );

            history.go(0);
          } else {
            // coupon not redeemed yet
            try {
              const resCoupon = await CouponService.redeem(couponCode);

              if (resCoupon && resCoupon.stampCard.id) {
                history.replace(
                  `/coupon/${couponCode}/merchant/${res.company.id}/status/success/msg/ok`,
                );
                history.go(0);
              }
            } catch (e) {
              console.log('Redeem Coupon Error: ', e);
              history.replace(
                `/coupon/${couponCode}/merchant/${res.company.id}/status/fail/msg/redeemed_failed`,
              );
              history.go(0);
            }
          }
        } catch (e) {
          console.log('Fetch Coupon Error: ', e);
          history.replace(
            `/merchant/0/coupon/${couponCode}/status/fail/msg/not_found`,
          );
          history.go(0);
        }
      }
    },
    [history],
  );

  const handleQRCodeScanned = useCallback(
    (decodedText: string) => {
      try {
        const action = parseDeepLink(decodedText);
        console.log('Parsed action from QR code:', action); // Debug log to check parsed action

        switch (action.type) {
          case ScanType.RedeemCoupon:
            if (action.payload.code) {
              handleCouponCode(action.payload.code);
              console.log('Redeem Coupon code:', action.payload.code); // Debug log for coupon code
            } else {
              throw new Error('Missing code for RedeemCoupon');
            }
            break;
          case ScanType.ViewMerchant:
            if (action.payload.merchantId) {
              history.replace(`/join_merchant/${action.payload.merchantId}`);
              history.go(0);
              console.log('View Merchant ID:', action.payload.merchantId); // Debug log for merchant ID
            } else {
              throw new Error('Missing merchantId for ViewMerchant');
            }
            break;
          case ScanType.Contactless:
            if (action.payload.contactless) {
              handleContactlessCode(action.payload.contactless);
              console.log('Contactless code:', action.payload.contactless); // Debug log for contactless code
            } else {
              throw new Error('Missing contactless code for Contactless');
            }
            break;
          case ScanType.SingleUse:
            if (action.payload.singleUseCode) {
              handleSingleUseCode(
                action.payload.singleUseCode,
                action.payload.merchantId || '',
                action.payload.stampNum || '',
                action.payload.storeId || '',
              );
            } else {
              throw new Error('Missing singleUseCode for SingleUse');
            }
            break;
          default:
            throw new Error('Unknown action type');
        }
      } catch (error) {
        if (onError) {
          onError(
            'Invalid or unsupported QR code. Please scan a valid Flex QR code.',
          );
        }
      }
    },
    [
      onError,
      handleContactlessCode,
      handleSingleUseCode,
      handleCouponCode,
      history,
    ],
  );

  useEffect(() => {
    let isMounted = true;
    let transitionPromise = Promise.resolve();
    const setupScanner = async () => {
      // Clean up any previous scanner instance before creating a new one
      if (scannerInstance.current) {
        try {
          if (scannerInstance.current.isScanning) {
            await scannerInstance.current.stop();
          }
          scannerInstance.current.clear();
        } catch (e) {
          // Ignore errors from stop/clear
        }
        scannerInstance.current = null;
      }
      if (!isMounted || !scannerRef.current) return;
      // Now create a new scanner instance
      scannerInstance.current = new Html5Qrcode('qr-scanner');
      try {
        await scannerInstance.current.start(
          { facingMode: 'environment' },
          {
            fps: 10,
            qrbox:
              window.innerWidth < 600 ? 250 : window.innerWidth * 0.8 * 0.7, // 70% of the smaller edge of the viewfinder
          },
          (decodedText) => {
            if (!hasScanned.current) {
              hasScanned.current = true;
              try {
                handleQRCodeScanned(decodedText);
              } catch (error) {
                if (onError) onError((error as Error).message);
              }
            }
          },
          (errorMessage) => {
            // Prevent repeated error dialog for known html5-qrcode bug
            if (
              errorMessage &&
              (errorMessage.includes('Cannot clear while scan is ongoing') ||
                errorMessage.includes(
                  'No MultiFormat Readers were able to detect the code',
                ) ||
                errorMessage.includes('No barcode or QR code detected'))
            ) {
              // Ignore this error, it's handled in cleanup or is just no QR code found
              return;
            }
            if (onError) onError(errorMessage);
          },
        );
      } catch (e) {
        if (onError) onError((e as Error).message);
      }
    };

    transitionPromise = setupScanner();

    return () => {
      isMounted = false;
      transitionPromise.then(async () => {
        if (scannerInstance.current) {
          try {
            if (scannerInstance.current.isScanning) {
              await scannerInstance.current.stop();
            }
            scannerInstance.current.clear();
          } catch (e) {
            // Ignore errors from stop/clear
          }
        }
      });
    };
  }, [onError, handleQRCodeScanned]);

  // Use most of device width for container

  return (
    <div
      id="qr-scanner"
      ref={scannerRef}
      style={
        window.innerWidth < 600
          ? { width: '100%' }
          : { width: '100%', height: window.innerWidth * 0.8 }
      }
    />
  );
};

export default QRScanner;
